import React, { forwardRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { usePrefs, useRequestData } from 'wsm-common-data';

import useSelect from '../../hooks/useSelect';
import useFormatNumber from '../../hooks/useFormatNumber';
import { optionType } from './type';
import Options from './Options';
import './facet_select.scss';
import { g } from '../../global-constants';
import { dropdownTracking } from '../../utilities/trackingFactory';

const FacetSelect = forwardRef(
	(
		{ facet = {}, onChange, renderBottom, id, inputClass, onVisibleChange },
		ref
	) => {
		const { values: defaultOptions = [], id: facetId } = facet;
		const selectedValue = defaultOptions.filter(
			(item) => item.selected === true
		);

		const { deviceType, widgetName, windowId } = useRequestData();

		const onOpenedDropDown = useCallback(() => {
			dropdownTracking(widgetName, windowId, facetId);
		}, [facetId, widgetName, windowId]);

		const isDesktop = deviceType === g.DESKTOP;
		const selectBoxClassess = isDesktop
			? 'desktop-select-box'
			: 'mobile-select-box';

		const selectedOption = selectedValue[0];
		const [snapshot, valueProps, optionProps] = useSelect({
			options: defaultOptions,
			onChange,
			onOpened: onOpenedDropDown,
			facetId,
			value: selectedOption?.value
		});
		const { showCountOnSubmitButton } = usePrefs();
		const formatNumber = useFormatNumber();

		const { focus, displayValue, options, option } = snapshot || {};

		const wrapperClass = setClassNames([
			'facet-select',
			focus && 'has-focus'
		]);

		const shouldRenderOptions = snapshot.focus;

		useEffect(() => {
			onVisibleChange(shouldRenderOptions);
		}, [shouldRenderOptions]);

		const inputProps = {
			...valueProps,
			value: displayValue
		};

		return (
			<div
				ref={ref}
				className={`${wrapperClass} ddc-bootstrap-select`}
				id={id}
				data-cy-option-length={options.length}
			>
				<div className="value">
					{/* The logic handling logic for dropdown visible relaying on focus and blur events
					Anchor element is used instead of button because the button is not focused on click on Safari lead to inconsistent behavior.
					Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus  */}
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
					<a
						role="button"
						{...inputProps}
						className={`${inputClass} ${selectBoxClassess}`}
					>
						<span>
							<span>{displayValue}</span>
							<small className="ml-2 text-neutral-600">
								{showCountOnSubmitButton === 'false' &&
									option &&
									option.count &&
									formatNumber(option.count)}
							</small>
							<i className="ddc-icon ddc-icon-arrow-select" />
						</span>
					</a>
					{shouldRenderOptions && (
						<Options
							facetId={facetId}
							options={options}
							optionProps={optionProps}
							snapshot={snapshot}
							renderBottom={renderBottom}
						/>
					)}
				</div>
				<div className="facet-select-border-right" />
				<div className="facet-select-border-left" />
			</div>
		);
	}
);

FacetSelect.propTypes = {
	// Data
	facet: PropTypes.shape({
		values: PropTypes.arrayOf({ optionType }),
		id: PropTypes.string,
		allLabel: PropTypes.string,
		label: PropTypes.string
	}),

	// Attributes
	id: PropTypes.string,

	// Renderers
	renderBottom: PropTypes.func,

	// Events
	onChange: PropTypes.func,
	inputClass: PropTypes.string,
	onVisibleChange: PropTypes.func
};

export default FacetSelect;
